import { c as _c } from "react/compiler-runtime";
import { IcoLock24 } from '@onefootprint/icons';
import type { BBox, DocumentViewerCard } from '@onefootprint/request-types/dashboard';
import { Button, LoadingSpinner, useObjectUrl } from '@onefootprint/ui';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeBase64Strict } from '@/utils/decode-base64';
import type { DocumentUpload } from '../../document-viewer';
import ImageViewer from './components/image-viewer';
import PreviewNotAvailable from './components/preview-not-available';
import TextContentViewer from './components/text-content-viewer';

// Dynamic import with ssr: false to avoid DOMMatrix error during build
// react-pdf uses pdfjs-dist which requires browser APIs (canvas, DOMMatrix)
const PdfViewer = dynamic(() => import('./components/pdf-viewer'), {
  ssr: false
});
type Bbox = {
  id: string;
  bbox: BBox;
  pageId?: number;
};
type DocumentPreviewProps = {
  documentSource: string | null;
  cards: DocumentViewerCard[];
  selectedBboxIds: Set<string>;
  onBboxClick: (id: string, pageId?: number) => void;
  scrollToPageId?: number;
  onDecrypt?: () => Promise<void>;
  selectedUpload?: DocumentUpload;
  documentName?: string;
};
const EncryptedState = t0 => {
  const $ = _c(19);
  const {
    onDecrypt,
    isDecrypting
  } = t0;
  const {
    t
  } = useTranslation("document-viewer");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IcoLock24 />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <h3 className="text-label-2 text-primary">{t2}</h3>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp2);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="text-body-2 text-secondary">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="flex flex-col items-center gap-1">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isDecrypting || $[13] !== onDecrypt || $[14] !== t) {
    t7 = onDecrypt && <Button onClick={onDecrypt} loading={isDecrypting}>{t(_temp3)}</Button>;
    $[12] = isDecrypting;
    $[13] = onDecrypt;
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex flex-col items-center justify-center h-full w-full gap-4 p-6">{t1}{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
type PreviewBodyProps = {
  base64Data: string;
  objectUrl: string | null;
  mimeType: string | null;
  bboxes: Bbox[];
  selectedBboxIds: Set<string>;
  onBboxClick: (id: string, pageId?: number) => void;
  scrollToPageId?: number;
  documentName?: string;
};

// Renders a single decrypted blob by mime type. react-pdf needs a data URL (its worker can't read
// blob URLs), so PDFs pass the raw base64; images use the object URL. Text/JSON render as text;
// decodeBase64Strict returns null for binary so unrecognized content shows "preview not available".
const PreviewBody = t0 => {
  const $ = _c(21);
  const {
    base64Data,
    objectUrl,
    mimeType,
    bboxes,
    selectedBboxIds,
    onBboxClick,
    scrollToPageId,
    documentName
  } = t0;
  if (mimeType === "application/pdf") {
    const t1 = `data:application/pdf;base64,${base64Data}`;
    let t2;
    if ($[0] !== bboxes || $[1] !== documentName || $[2] !== onBboxClick || $[3] !== scrollToPageId || $[4] !== selectedBboxIds || $[5] !== t1) {
      t2 = <div className="h-full w-full"><PdfViewer src={t1} bboxes={bboxes} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} scrollToPageId={scrollToPageId} documentName={documentName} /></div>;
      $[0] = bboxes;
      $[1] = documentName;
      $[2] = onBboxClick;
      $[3] = scrollToPageId;
      $[4] = selectedBboxIds;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  if (mimeType?.startsWith("image/") && objectUrl) {
    let t1;
    if ($[7] !== bboxes || $[8] !== objectUrl || $[9] !== onBboxClick || $[10] !== selectedBboxIds) {
      t1 = <div className="h-full w-full"><ImageViewer src={objectUrl} bboxes={bboxes} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} /></div>;
      $[7] = bboxes;
      $[8] = objectUrl;
      $[9] = onBboxClick;
      $[10] = selectedBboxIds;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    return t1;
  }
  let t1;
  if ($[12] !== base64Data) {
    t1 = decodeBase64Strict(base64Data);
    $[12] = base64Data;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const textContent = t1;
  if (textContent) {
    let t2;
    if ($[14] !== mimeType || $[15] !== textContent) {
      t2 = <TextContentViewer text={textContent} mimeType={mimeType} />;
      $[14] = mimeType;
      $[15] = textContent;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    return t2;
  }
  let t2;
  if ($[17] !== base64Data || $[18] !== documentName || $[19] !== mimeType) {
    t2 = <PreviewNotAvailable base64Data={base64Data} mimeType={mimeType} documentName={documentName} />;
    $[17] = base64Data;
    $[18] = documentName;
    $[19] = mimeType;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  return t2;
};

// Collect all bboxes from cards that have bounding box content
const collectBboxes = (cards: DocumentViewerCard[]) => {
  const bboxes: Bbox[] = [];
  for (const card of cards) {
    if (card.content.contentType === 'bounding_box') {
      for (const group of card.content.bboxGroups) {
        for (const bboxItem of group.bboxes) {
          if (bboxItem.bbox) {
            bboxes.push({
              id: bboxItem.id,
              bbox: bboxItem.bbox,
              pageId: bboxItem.pageId
            });
          }
        }
      }
    }
  }
  return bboxes;
};
const DocumentPreview = ({
  documentSource,
  cards,
  selectedBboxIds,
  onBboxClick,
  scrollToPageId,
  onDecrypt,
  selectedUpload,
  documentName
}: DocumentPreviewProps) => {
  const {
    objectUrl,
    mimeType
  } = useObjectUrl(documentSource);
  const {
    objectUrl: uploadObjectUrl,
    mimeType: uploadMimeType
  } = useObjectUrl(selectedUpload?.imageData ?? null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const handleDecrypt = async () => {
    if (!onDecrypt) return;
    setIsDecrypting(true);
    try {
      await onDecrypt();
    } finally {
      setIsDecrypting(false);
    }
  };
  const bboxes = collectBboxes(cards);
  const encryptedState = <EncryptedState onDecrypt={onDecrypt ? handleDecrypt : undefined} isDecrypting={isDecrypting} />;

  // A selected upload renders directly from its own decrypted bytes.
  if (selectedUpload) {
    if (selectedUpload.imageData === null) return encryptedState;
    return <PreviewBody key={`${selectedUpload.side}-${selectedUpload.timestamp}`} base64Data={selectedUpload.imageData} objectUrl={uploadObjectUrl} mimeType={uploadMimeType} bboxes={bboxes} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} scrollToPageId={scrollToPageId} documentName={documentName} />;
  }
  if (documentSource === null) return encryptedState;
  if (!objectUrl) {
    return <div className="flex items-center justify-center h-full w-full">
        <LoadingSpinner />
      </div>;
  }
  return <PreviewBody base64Data={documentSource} objectUrl={objectUrl} mimeType={mimeType} bboxes={bboxes} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} scrollToPageId={scrollToPageId} documentName={documentName} />;
};
export default DocumentPreview;
function _temp($) {
  return $.preview.encrypted.title;
}
function _temp2($_0) {
  return $_0.preview.encrypted.description;
}
function _temp3($_1) {
  return $_1.preview.encrypted.decryptButton;
}